import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Check, Phone, MessageSquare, Search, LayoutDashboard, History, Users } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderBadge from '@/components/editorial/AnimatedBorderBadge';

const EditorialAutomateSection: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isVisible, setIsVisible] = useState(false);
  const [showConversations, setShowConversations] = useState<number[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [typingIndicator, setTypingIndicator] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const features = [
    { text: "Unified Command Center", icon: LayoutDashboard },
    { text: "Missed Call Text-Back", icon: Phone },
    { text: "Smart Lists & Segmentation", icon: Users },
    { text: "Consolidated Client History", icon: History },
    { text: "Centralized Communication", icon: MessageSquare }
  ];

  const conversations = [
    { name: 'Sarah M.', message: 'Hey! I saw your ad and wanted to learn more...', platform: 'instagram', platformIcon: '📸', time: '2m', unread: true },
    { name: 'Mike T.', message: 'Thanks for the quick response! Can we schedule...', platform: 'email', platformIcon: '📧', time: '5m', unread: true },
    { name: 'Emma R.', message: 'Perfect, I\'ll see you tomorrow at 3pm!', platform: 'sms', platformIcon: '💬', time: '12m', unread: false },
    { name: 'John D.', message: 'Do you have availability this week?', platform: 'whatsapp', platformIcon: '📱', time: '18m', unread: true },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Animate conversations appearing
    conversations.forEach((_, index) => {
      setTimeout(() => {
        setShowConversations(prev => [...prev, index]);
      }, 400 + index * 200);
    });

    // Animate unread count
    const unreadTimer = setInterval(() => {
      setUnreadCount(prev => {
        if (prev >= 4) return 4;
        return prev + 1;
      });
    }, 200);

    // Show typing indicator on one conversation
    setTimeout(() => setTypingIndicator(0), 2000);
    setTimeout(() => setTypingIndicator(null), 3500);

    return () => clearInterval(unreadTimer);
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-16 px-[7%] sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Visual - Unified Inbox */}
          <div className="order-2 lg:order-1">
            <div 
              className="rounded-xl p-6 overflow-hidden"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8', 
                border: isDark ? '1px solid #333333' : '1px solid #E5E5E5',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(-4px) scale(1)' : 'translateY(20px) scale(0.95)',
                boxShadow: isVisible ? (isDark ? '0 20px 40px -10px rgba(0, 0, 0, 0.5)' : '0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.1)') : 'none',
                transition: 'all 0.6s ease-out'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" style={{ color: isDark ? '#AAAAAA' : '#555555' }} />
                  <span className="text-sm font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>Unified Inbox</span>
                </div>
                <div className="flex items-center gap-2">
                  <span 
                    className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${isDark ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600'}`}
                    style={{ animation: unreadCount > 0 ? 'pulse 1.5s ease-in-out infinite' : 'none' }}
                  >
                    {unreadCount} unread
                  </span>
                </div>
              </div>

              {/* Search Bar */}
              <div 
                className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4"
                style={{ 
                  backgroundColor: isDark ? '#222222' : '#FFFFFF', 
                  border: isDark ? '1px solid #333333' : '1px solid #E0E0E0',
                  opacity: isVisible ? 1 : 0,
                  transition: 'opacity 0.5s ease-out 0.3s'
                }}
              >
                <Search className="w-3 h-3" style={{ color: isDark ? '#666666' : '#888888' }} />
                <span className="text-[10px]" style={{ color: isDark ? '#666666' : '#888888' }}>Search all conversations...</span>
              </div>

              {/* Channel Filters */}
              <div className="flex gap-2 mb-4 overflow-x-auto">
                {[
                  { label: 'All', count: 24, active: true },
                  { label: 'DMs', count: 8, active: false },
                  { label: 'Email', count: 6, active: false },
                  { label: 'SMS', count: 5, active: false },
                  { label: 'WhatsApp', count: 5, active: false }
                ].map((filter, index) => (
                  <div 
                    key={index}
                    className="px-2 py-1 rounded text-[9px] font-medium whitespace-nowrap"
                    style={{ 
                      backgroundColor: filter.active 
                        ? (isDark ? '#FFFFFF' : '#111111') 
                        : (isDark ? '#333333' : '#F0F0F0'),
                      color: filter.active 
                        ? (isDark ? '#000000' : '#FFFFFF') 
                        : (isDark ? '#AAAAAA' : '#666666'),
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateY(0)' : 'translateY(5px)',
                      transition: `all 0.3s ease-out ${200 + index * 50}ms`
                    }}
                  >
                    {filter.label} ({filter.count})
                  </div>
                ))}
              </div>

              {/* Conversation list */}
              <div className="space-y-2">
                {conversations.map((conv, index) => (
                  <div 
                    key={index}
                    className="rounded-lg p-3 flex items-start gap-3 cursor-pointer transition-all hover:shadow-sm relative"
                    style={{ 
                      backgroundColor: conv.unread 
                        ? (isDark ? '#222222' : '#FFFFFF') 
                        : (isDark ? '#1A1A1A' : '#FAFAFA'), 
                      border: `1px solid ${conv.unread 
                        ? (isDark ? '#333333' : '#E0E0E0') 
                        : (isDark ? '#2A2A2A' : '#EEEEEE')}`,
                      opacity: showConversations.includes(index) ? 1 : 0,
                      transform: showConversations.includes(index) ? 'translateX(0)' : 'translateX(-20px)',
                      transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }}
                  >
                    <div 
                      className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center flex-shrink-0"
                      style={{
                        animation: showConversations.includes(index) ? 'bounceIn 0.5s ease-out' : 'none'
                      }}
                    >
                      <span className="text-xs font-medium text-white">{conv.name.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{conv.name}</span>
                          <span className="text-xs">{conv.platformIcon}</span>
                        </div>
                        <span className="text-[10px]" style={{ color: isDark ? '#666666' : '#888888' }}>{conv.time}</span>
                      </div>
                      {typingIndicator === index ? (
                        <div className="flex items-center gap-1">
                          <div className="flex gap-0.5">
                            {[0, 1, 2].map(i => (
                              <div 
                                key={i}
                                className="w-1 h-1 rounded-full"
                                style={{
                                  backgroundColor: isDark ? '#666666' : '#888888',
                                  animation: `typingBounce 1s ease-in-out ${i * 0.15}s infinite`
                                }}
                              />
                            ))}
                          </div>
                          <span className="text-[10px] italic" style={{ color: isDark ? '#666666' : '#888888' }}>typing...</span>
                        </div>
                      ) : (
                        <p className="text-[10px] truncate" style={{ color: isDark ? '#AAAAAA' : '#555555' }}>{conv.message}</p>
                      )}
                    </div>
                    {conv.unread && (
                      <div 
                        className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1"
                        style={{ animation: 'pulse 2s ease-in-out infinite' }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Quick Stats */}
              <div className="mt-4 grid grid-cols-4 gap-2">
                {[
                  { icon: '📸', label: 'Instagram', count: 8 },
                  { icon: '📧', label: 'Email', count: 6 },
                  { icon: '💬', label: 'SMS', count: 5 },
                  { icon: '📱', label: 'WhatsApp', count: 5 }
                ].map((channel, index) => (
                  <div 
                    key={index}
                    className="text-center p-2 rounded-lg"
                    style={{ 
                      backgroundColor: isDark ? '#222222' : '#FFFFFF', 
                      border: isDark ? '1px solid #333333' : '1px solid #E0E0E0',
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.9)',
                      transition: `all 0.4s ease-out ${1200 + index * 100}ms`
                    }}
                  >
                    <span className="text-sm">{channel.icon}</span>
                    <div className="text-[9px] font-medium mt-0.5" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{channel.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="order-1 lg:order-2">
            {/* Badge with animated border */}
            <AnimatedBorderBadge text="Inbox" icon={MessageCircle} align="left" />

            <h2 
              className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              One Thread to Rule Them All.
            </h2>

            <p 
              className="text-sm mb-8"
              style={{ color: isDark ? '#AAAAAA' : '#555555' }}
            >
              Every DM, SMS, Email, and WhatsApp message in one single, searchable inbox.
            </p>

            {/* Feature checklist */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${index * 100}ms`
                  }}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                    <Check className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  </div>
                  <span className="text-sm" style={{ color: isDark ? '#CCCCCC' : '#333333' }}>{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] hover:opacity-90"
              style={{
                backgroundColor: isDark ? '#FFFFFF' : '#000000',
                color: isDark ? '#000000' : '#FFFFFF',
              }}
            >
              Open Unified Inbox →
            </button>
          </div>
        </div>
      </div>

    </section>
  );
};

export default EditorialAutomateSection;
